import { useEffect } from 'react';
import { useAxios } from 'src/hooks/use-axios';
import { useCognitoUser } from 'src/hooks/use-cognito';
import { useOptionalMonolithCurrentUser } from 'src/hooks/use-monolith-user';
import { OrganisationServiceRoot } from 'src/types/organisation-service';

function useOrganisationServiceRaw() {
  return useAxios(
    (axios, signal) =>
      axios.request<OrganisationServiceRoot>({
        signal,
        url: PRISMA_CONFIG.organisationService,
      }),
    { neededOnPageLoad: true, cacheKey: 'organisation-service-root' }
  );
}

type OrganisationServiceRawRequest = ReturnType<
  typeof useOrganisationServiceRaw
>;

// function overloading as the request might be optional
export function useOrganisationService(options: {
  onlyIfAuthenticated: true;
}): OrganisationServiceRawRequest | undefined;
export function useOrganisationService(): OrganisationServiceRawRequest;
export function useOrganisationService({ onlyIfAuthenticated = false } = {}) {
  const auth = useOptionalMonolithCurrentUser();
  const user = useCognitoUser();
  const isAuthenticated = Boolean(auth || user.data);

  const organisationService = useOrganisationServiceRaw();

  useEffect(() => {
    if (onlyIfAuthenticated && !isAuthenticated) return;
    organisationService.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (onlyIfAuthenticated && !isAuthenticated) return;

  return organisationService;
}

export type OrganisationServiceRequest = ReturnType<
  typeof useOrganisationService
>;

export function useOrganisationServiceIfAuthenticated() {
  const organisationService = useAxios(
    (axios, signal) =>
      axios.request<OrganisationServiceRoot>({
        signal,
        url: PRISMA_CONFIG.organisationService,
      }),
    {
      neededOnPageLoad: true,
      cacheKey: 'organisation-service-root-2',
      onError(error) {
        return error;
      },
    }
  );

  useEffect(() => {
    organisationService.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return organisationService;
}
